// API client pour les biens immobiliers
export interface CreatePropertyData {
  type: string;
  address: string;
  city: string;
  wilaya: string;
  price: number;
  net_price: number;
  commission: number;
  status: 'for-sale' | 'for-rent' | 'for-short-rent' | 'sold' | 'rented' | 'archived' | 'estimation';
  area: number;
  rooms: number;
  bedrooms?: number;
  living_room_area?: number;
  usable_area?: number;
  floor?: number;
  construction_year?: number;
  has_elevator?: boolean;
  has_parking?: boolean;
  has_balcony?: boolean;
  has_terrace?: boolean;
  has_air_conditioning?: boolean;
  has_cistern?: boolean;
  has_water_tank?: boolean;
  has_double_glazing?: boolean;
  has_intercom?: boolean;
  has_interphone?: boolean;
  has_video_surveillance?: boolean;
  has_trustee?: boolean;
  has_digicode?: boolean;
  has_guardian?: boolean;
  heating_type?: string;
  description?: string;
  owner_name: string;
  owner_phone?: string;
  owner_email?: string;
  property_category: string;
  property_subtype: string;
  publish_dar_dz?: boolean;
  publish_ouedkniss?: boolean;
}

export interface UpdatePropertyData extends Partial<CreatePropertyData> {
  status?: 'for-sale' | 'for-rent' | 'for-short-rent' | 'sold' | 'rented' | 'archived' | 'estimation';
}

export interface Property {
  id: number;
  agency_id: number;
  created_by_user_id: number;
  type: string;
  address: string;
  city: string;
  wilaya: string;
  price: number;
  net_price: number;
  commission: number;
  status: 'for-sale' | 'for-rent' | 'for-short-rent' | 'sold' | 'rented' | 'archived' | 'estimation';
  area: number;
  rooms: number;
  bedrooms?: number;
  living_room_area?: number;
  usable_area?: number;
  floor?: number;
  construction_year?: number;
  has_elevator: boolean;
  has_parking: boolean;
  has_balcony: boolean;
  has_terrace: boolean;
  has_air_conditioning: boolean;
  heating_type?: string;
  description?: string;
  owner_name: string;
  owner_phone?: string;
  owner_email?: string;
  publish_dar_dz: boolean;
  publish_ouedkniss: boolean;
  is_published: boolean; // Calculé automatiquement
  photos: string[];
  features: {
    area: number;
    rooms: number;
    floor?: number;
    hasElevator: boolean;
    hasParking: boolean;
    hasBalcony: boolean;
    hasTerrace: boolean;
    hasAirConditioning: boolean;
    heatingType?: string;
    constructionYear?: number;
  };
  owner: {
    name: string;
    phone?: string;
    email?: string;
  };
  agent: string;
  netPrice: number;
  publishToDarDz: boolean;
  publishToOuedkniss: boolean;
  isPublished: boolean; // Calculé automatiquement
  createdAt: Date;
  updatedAt: Date;
  property_category: string;
  property_subtype: string;
  agency_name?: string;
  created_by_name?: string;
}

class PropertyAPI {
  private baseUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'}/properties`;

  async getAll(filters?: {
    agency_id?: number;
    status?: string;
    type?: string;
    city?: string;
    wilaya?: string;
    min_price?: number;
    max_price?: number;
  }) {
    const params = new URLSearchParams();
    if (filters?.agency_id) params.append('agency_id', filters.agency_id.toString());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.city) params.append('city', filters.city);
    if (filters?.wilaya) params.append('wilaya', filters.wilaya);
    if (filters?.min_price) params.append('min_price', filters.min_price.toString());
    if (filters?.max_price) params.append('max_price', filters.max_price.toString());

    const url = params.toString() ? `${this.baseUrl}?${params}` : this.baseUrl;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch properties');
    return response.json();
  }

  async create(data: CreatePropertyData, agency_id: number, created_by_user_id: number) {
    const payload = {
      ...data,
      agency_id,
      created_by_user_id
    };

    console.log('🚀 Envoi vers API:', JSON.stringify(payload, null, 2));

    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    
    console.log('📡 Statut de réponse:', response.status);
    console.log('📋 Headers de réponse:', Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      let error;
      try {
        const responseText = await response.text();
        console.log('📄 Réponse brute:', responseText);
        
        if (responseText) {
          error = JSON.parse(responseText);
        } else {
          error = { error: 'Réponse vide du serveur' };
        }
      } catch (parseError) {
        console.error('❌ Erreur de parsing JSON:', parseError);
        error = { error: 'Réponse invalide du serveur', details: parseError.message };
      }
      
      console.error('❌ Erreur API:', error);
      throw new Error(error.error || error.message || 'Failed to create property');
    }
    
    try {
      const result = await response.json();
      console.log('✅ Bien créé avec succès:', result.id);
      return result;
    } catch (parseError) {
      console.error('❌ Erreur de parsing de la réponse de succès:', parseError);
      throw new Error('Réponse invalide du serveur');
    }
  }

  async update(id: number, data: UpdatePropertyData, agency_id?: number) {
    console.log('🔄 PropertyAPI.update appelé pour ID:', id);
    console.log('📦 Données à envoyer:', Object.keys(data));

    const payload = agency_id ? { ...data, agency_id } : data;
    
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    
    console.log('📡 Statut de réponse PUT:', response.status);
    
    if (!response.ok) {
      let error;
      try {
        const responseText = await response.text();
        console.log('📄 Réponse d\'erreur brute:', responseText);
        error = responseText ? JSON.parse(responseText) : { error: 'Réponse vide' };
      } catch (parseError) {
        console.error('❌ Erreur de parsing de la réponse:', parseError);
        error = { error: 'Réponse invalide du serveur' };
      }
      console.error('❌ Erreur API détaillée:', error);
      throw new Error(error.error || error.message || 'Failed to update property');
    }
    
    console.log('✅ Mise à jour réussie côté API');
    return response.json();
  }

  async delete(id: number) {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete property');
    }
    return response.json();
  }

  async archive(id: number) {
    return this.update(id, { status: 'archived' });
  }

  async publish(id: number) {
    return this.update(id, { is_published: true });
  }

  async unpublish(id: number) {
    return this.update(id, { is_published: false });
  }
}

export const propertyAPI = new PropertyAPI();